import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';

import FitmentShopProductsButtons from '../../../../../../components/fitment-shop-products-buttons/fitment-shop-products-buttons';
import Message from '../../../../../../components/message/message';
import OptionalSizes from './components/optional-sizes/optional-sizes';
import ExclamationTriangleSolidIcon from '../../../../../../shared/icons/exclamation-triangle-solid-icon';
import { LightBulbRegularIcon } from '../../../../../../shared/icons/icons';
import Button from '../../../../../button/button';
import Collapsible from '../../../../../collapsible/collapsible';
import {
  CANT_FIND_OPTION_IDS,
  EVENTS,
  I_CANT_FIND_SIZE_DROPDOWN_OPTION,
  OPTIONAL_TIRE_SIZE_DROPDOWN_OPTION,
  OPTIONAL_TIRE_SIZE_ID,
  PROP_TYPES,
  TABS
} from '../../../../fitment-constants';
import FitmentTireSizeHelpGuide from '../../../fitment-tire-size-help-guide/fitment-tire-size-help-guide';
import { assemblyPropType } from '../../fitment-by-vehicle-util';
import FitmentNoResultsFound from '../fitment-no-results-found/fitment-no-results-found';
import FitmentSelect from '../fitment-select/fitment-select';
import FitmentStepContainer from '../fitment-step-container/fitment-step-container';
import FitmentTireSizeOeDropdownOption from '../fitment-tire-size-oe-dropdown-option/fitment-tire-size-oe-dropdown-option';

import {
  useFeatureFlags,
  useMediaQuery,
  usePrevious,
  useProductSearchNavigation
} from '../../../../../../shared/hooks/hooks';

import { isArrayWithLength } from '../../../../../../shared/utils/validators/validators';

import { FEATURE_FLAGS } from '../../../../../../shared/constants/feature-flags';
import { ATV_VEHICLE_TYPE } from '../../../../../../shared/constants/fitment';

import './fitment-tire-size.scss';

function FitmentTireSize(props) {
  const {
    handleOeOptionSelect,
    handleReturnButton,
    isEditing,
    isLicensePlateSizeSearch,
    isLoadingOptionalSizes,
    isOptionalSizesOpen,
    isViewingOptionalSizes,
    onEvent,
    onFocusDropdown,
    onIStillCantFindTireSizeLinkClick,
    onShopBySelected,
    optionalSizes,
    pendingFitment,
    renderShopProductsButton,
    selectOptionalSize,
    selectedAssembly,
    selectedFitment,
    selectedOeOption,
    selectedOptionalSize,
    selectedOptionalSizeGrouping,
    selectedRideHeight,
    selectedTab,
    setIsOptionalSizesOpen,
    setIsViewingOptionalSizes,
    setPendingFitment,
    setSelectedOeOption,
    setSelectedOptionalSizeGrouping,
    setShowHelpContent,
    setShowSizeHelp,
    shopProducts,
    showCustomOptions = false,
    showHelpContent,
    showShopProductsButton = true,
    showSizeHelp,
    staggeredAssemblies,
    standardAssemblies,
    textOverrides
  } = props;

  const { featureFlags } = useFeatureFlags();
  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [showATVAlertMessage, setShowATVAlertMessage] = useState(false);
  const [isFitmentInitialized, setIsFitmentInitialized] = useState(false);

  const { onShopAllTires } = useProductSearchNavigation({
    fitment: selectedFitment
  });
  const [shopProductsWrapperStyle, setShopProductsWrapperStyle] = useState('');
  const shopAllProductsButtonParentRef = useRef(null);

  const { isMinMd } = useMediaQuery();
  const previousSelectedTab = usePrevious(selectedTab);

  const hasCustomTireSize = selectedOeOption?.value === OPTIONAL_TIRE_SIZE_ID;

  const isContinueDisabled =
    (!selectedAssembly && !isViewingOptionalSizes) ||
    (isViewingOptionalSizes && !selectedOptionalSize);

  const isViewOptionalSizesDisabled = !selectedOeOption?.value;

  const checkIfButtonShouldBeFixed = useCallback(() => {
    const fixedStyleButtonWrapper =
      !isMinMd &&
      shopAllProductsButtonParentRef?.current?.getBoundingClientRect()?.y +
        120 <
        0
        ? 'fixed-button-wrapper'
        : '';
    if (shopProductsWrapperStyle !== fixedStyleButtonWrapper) {
      setShopProductsWrapperStyle(fixedStyleButtonWrapper);
    }
  }, [isMinMd, shopProductsWrapperStyle]);

  const oeSizeOptions = useMemo(() => {
    const assemblyOptions = [];

    if (isArrayWithLength(standardAssemblies)) {
      assemblyOptions.push(
        ...standardAssemblies.map(assembly => ({
          label: assembly.description,
          value: assembly.id
        }))
      );
    }

    if (isArrayWithLength(staggeredAssemblies)) {
      assemblyOptions.push(
        ...staggeredAssemblies.map(assembly => ({
          label: assembly.description,
          value: assembly.id
        }))
      );
    }

    if (showCustomOptions) {
      assemblyOptions.push(OPTIONAL_TIRE_SIZE_DROPDOWN_OPTION);
      assemblyOptions.push(I_CANT_FIND_SIZE_DROPDOWN_OPTION);
    }

    return assemblyOptions;
  }, [standardAssemblies, staggeredAssemblies, showCustomOptions]);

  useEffect(() => {
    if (selectedAssembly) {
      setSelectedOeOption({
        label: selectedAssembly.description,
        value: selectedAssembly.id
      });
      setShowSizeHelp(false);
    }
  }, [selectedAssembly, setSelectedOeOption, setShowSizeHelp]);

  useEffect(() => {
    window.addEventListener('scroll', checkIfButtonShouldBeFixed);
    return () =>
      window.removeEventListener('scroll', checkIfButtonShouldBeFixed);
  }, [checkIfButtonShouldBeFixed]);

  useEffect(() => {
    if (
      selectedTab === TABS.VEHICLE &&
      selectedOeOption?.value === OPTIONAL_TIRE_SIZE_ID &&
      previousSelectedTab === TABS.SIZE &&
      !selectedAssembly
    ) {
      setSelectedOeOption(null);
    }
  }, [
    previousSelectedTab,
    selectedAssembly,
    selectedOeOption?.value,
    selectedTab,
    setSelectedOeOption
  ]);

  useEffect(() => {
    setShowATVAlertMessage(false);
    setIsFitmentInitialized(false);
  }, [setPendingFitment, setShowATVAlertMessage]);

  useEffect(() => {
    if (!isFitmentInitialized) {
      setPendingFitment({});
    }
  }, [isFitmentInitialized, setPendingFitment]);

  useEffect(() => {
    if (pendingFitment?.type === ATV_VEHICLE_TYPE) {
      setShowATVAlertMessage(true);
    } else {
      setShowATVAlertMessage(false);
    }
    setIsUpdateLoading(false);
  }, [pendingFitment, setIsUpdateLoading, setShowATVAlertMessage]);

  useEffect(() => {
    if (isFitmentInitialized) {
      setPendingFitment(selectedFitment);
    }
  }, [isFitmentInitialized, selectedFitment, setPendingFitment]);

  useEffect(() => {
    if (isLicensePlateSizeSearch) {
      setIsOptionalSizesOpen(true);
    } else {
      setIsOptionalSizesOpen(false);
    }
  }, [isLicensePlateSizeSearch, setIsOptionalSizesOpen]);

  return (
    <FitmentStepContainer
      containerRef={shopAllProductsButtonParentRef}
      heading={
        !isLicensePlateSizeSearch ? (
          <>
            {isFitmentRedesign ? (
              <>What is your vehicle tire size?</>
            ) : (
              <>
                {isViewingOptionalSizes ? (
                  <>Optional Tire Sizes</>
                ) : (
                  <>
                    What is the <strong>original</strong> tire size for your
                    vehicle?
                  </>
                )}
              </>
            )}
          </>
        ) : null
      }
      onMoreInfoContentClick={
        showCustomOptions
          ? null
          : () => {
              onEvent?.({
                name: EVENTS.VEHICLE_FITMENT_ASSEMBLY_NEED_HELP
              });
            }
      }
      styleName={`fitment-tire-size ${
        hasCustomTireSize && !selectedAssembly
          ? 'fitment-tire-size--no-margin'
          : ''
      }`}
    >
      {!isLicensePlateSizeSearch && (
        <>
          <div
            className="fitment-tire-size__oe-sizes-wrapper"
            styleName="oe-sizes-wrapper"
          >
            <FitmentSelect
              hideHeading
              isDisabled={isViewingOptionalSizes}
              onChange={handleOeOptionSelect}
              onFocus={onFocusDropdown}
              options={oeSizeOptions}
              placeholder="Tire Sizes"
              renderNoResultsFound={
                showCustomOptions
                  ? () => (
                      <FitmentNoResultsFound
                        onClick={() => {
                          handleOeOptionSelect({
                            value: CANT_FIND_OPTION_IDS.SIZE
                          });
                          document.activeElement?.blur();
                        }}
                      />
                    )
                  : undefined
              }
              renderOption={option => (
                <FitmentTireSizeOeDropdownOption isOption {...option} />
              )}
              renderValue={value => (
                <FitmentTireSizeOeDropdownOption
                  {...value}
                  styleName="oe-size-dropdown-selected-option"
                />
              )}
              selectedOption={selectedOeOption}
              styleName="oe-sizes-dropdown"
            />
            {selectedOeOption?.value !== CANT_FIND_OPTION_IDS.SIZE &&
              selectedOeOption?.value !== OPTIONAL_TIRE_SIZE_ID && (
                <div
                  className="fitment-tire-size__oe-definition-container"
                  styleName="oe-definition-container"
                >
                  <strong styleName="oe-highlight">O.E</strong> refers to
                  Original Equipment tire size that come standard on a vehicle
                  from the factory.
                </div>
              )}
            {selectedOeOption?.value !== OPTIONAL_TIRE_SIZE_ID && (
              <>
                <hr styleName="divider" />
                <div styleName="oe-sizes-help-wrapper">
                  <Collapsible
                    isOpen={showHelpContent}
                    label={
                      <div styleName="help-button-label">
                        <LightBulbRegularIcon styleName="blub-icon" />
                        <span styleName="tire-help-label">
                          Need help finding tire size?
                        </span>
                      </div>
                    }
                    onToggle={isOpen => setShowHelpContent(isOpen)}
                    toggleType="CHEVRON"
                  >
                    <FitmentTireSizeHelpGuide
                      onShopByClick={onShopBySelected}
                      showICantFindSizeOption={
                        selectedOeOption?.value === CANT_FIND_OPTION_IDS.SIZE
                      }
                    />
                  </Collapsible>
                </div>
              </>
            )}

            {showSizeHelp && (
              <>
                <hr styleName="divider" />
                <Button
                  onClick={onIStillCantFindTireSizeLinkClick}
                  styleName="still-cant-find-button"
                >
                  I still can’t find my tire size
                </Button>
              </>
            )}
          </div>
          {!showSizeHelp && !hasCustomTireSize && <hr styleName="divider" />}
        </>
      )}

      {isLicensePlateSizeSearch && (
        <Message
          isWarning
          message="Choose your optional tire size below"
          styleName="optional-size-warning"
        />
      )}

      {(isArrayWithLength(optionalSizes) ||
        !selectedAssembly ||
        isLoadingOptionalSizes) &&
        !showSizeHelp &&
        !hasCustomTireSize && (
          <>
            {(isViewingOptionalSizes || isOptionalSizesOpen) && (
              <OptionalSizes
                isStaggered={selectedFitment?.isStaggered}
                optionalSizes={optionalSizes}
                selectedAssembly={selectedAssembly}
                selectedOptionalSize={selectedOptionalSize}
                selectedOptionalSizeGrouping={selectedOptionalSizeGrouping}
                selectedRideHeight={selectedRideHeight}
                setSelectedOptionalSize={selectOptionalSize}
                setSelectedOptionalSizeGrouping={
                  setSelectedOptionalSizeGrouping
                }
              />
            )}
          </>
        )}

      {showATVAlertMessage && (
        <div styleName="atv-alert-container">
          <div styleName="atv-alert-message">
            <ExclamationTriangleSolidIcon styleName="atv-alert-icon" />
            <span styleName="alert-message-text">
              Treadwell isn&apos;t testing ATV tires yet. <br /> You can still
              shop by your tire size.
            </span>
          </div>
          <Button
            isFullWidth
            isPrimary
            onClick={() => {
              onShopAllTires?.();
            }}
          >
            Shop All Tires
          </Button>
        </div>
      )}

      {showShopProductsButton &&
        !showSizeHelp &&
        !hasCustomTireSize &&
        !showATVAlertMessage && (
          <>
            {renderShopProductsButton ? (
              renderShopProductsButton({
                className: 'fitment-tire-size__shop-products-button',
                hideCancelButton: isViewingOptionalSizes,
                isDisabled: isContinueDisabled,
                isEditing: isEditing,
                isViewDisabled: isViewOptionalSizesDisabled,
                onClick: () => {
                  setIsFitmentInitialized(true);
                  setIsUpdateLoading(true);
                  shopProducts();
                },
                onReturnClick: handleReturnButton,
                onViewClick: () => setIsViewingOptionalSizes(true),
                showReturn: isViewingOptionalSizes,
                showSpinner: isUpdateLoading,
                showView:
                  !isViewingOptionalSizes && isArrayWithLength(optionalSizes),
                showViewLabel: selectedRideHeight
              })
            ) : (
              <div
                styleName={`${shopProductsWrapperStyle} shop-products-wrapper`}
              >
                <FitmentShopProductsButtons
                  buttonText={
                    textOverrides?.fitmentContinueCta ?? 'Shop Products'
                  }
                  isDisabled={isContinueDisabled}
                  isViewDisabled={isViewOptionalSizesDisabled}
                  onClick={() => {
                    setIsFitmentInitialized(true);
                    setIsUpdateLoading(true);
                    shopProducts();
                  }}
                  onReturnClick={handleReturnButton}
                  onViewClick={() => setIsViewingOptionalSizes(true)}
                  selectedRideHeight={selectedRideHeight}
                  showReturn={isViewingOptionalSizes}
                  showSpinner={isUpdateLoading}
                  showView={
                    !isViewingOptionalSizes && isArrayWithLength(optionalSizes)
                  }
                />
              </div>
            )}
          </>
        )}
    </FitmentStepContainer>
  );
}

FitmentTireSize.propTypes = {
  handleOeOptionSelect: PropTypes.func,
  handleReturnButton: PropTypes.func,
  isEditing: PropTypes.bool,
  isLicensePlateSizeSearch: PropTypes.bool,
  isLoadingOptionalSizes: PropTypes.bool,
  isOptionalSizesOpen: PropTypes.bool,
  isViewingOptionalSizes: PropTypes.bool,
  onEvent: PropTypes.func,
  onFocusDropdown: PropTypes.func,
  onIStillCantFindTireSizeLinkClick: PropTypes.func,
  onShopBySelected: PropTypes.func,
  optionalSizes: PropTypes.arrayOf(PropTypes.object),
  pendingFitment: PROP_TYPES.fitment,
  renderShopProductsButton: PropTypes.func,
  selectOptionalSize: PropTypes.func,
  selectedAssembly: assemblyPropType,
  selectedFitment: PropTypes.shape({
    frontTireSize: PropTypes.string,
    isOE: PropTypes.bool,
    isStaggered: PropTypes.bool,
    rearTireSize: PropTypes.string,
    sizeQualifier: PropTypes.string
  }),
  selectedOeOption: PropTypes.object,
  selectedOptionalSize: PropTypes.object,
  selectedOptionalSizeGrouping: PropTypes.object,
  selectedRideHeight: PropTypes.object,
  selectedTab: PropTypes.string,
  setIsOptionalSizesOpen: PropTypes.func,
  setIsViewingOptionalSizes: PropTypes.func,
  setPendingFitment: PropTypes.func,
  setSelectedOeOption: PropTypes.func,
  setSelectedOptionalSizeGrouping: PropTypes.func,
  setShowHelpContent: PropTypes.func,
  setShowSizeHelp: PropTypes.func,
  shopProducts: PropTypes.func,
  showCustomOptions: PropTypes.bool,
  showHelpContent: PropTypes.func,
  showShopProductsButton: PropTypes.bool,
  showSizeHelp: PropTypes.bool,
  staggeredAssemblies: PropTypes.arrayOf(PropTypes.object),
  standardAssemblies: PropTypes.arrayOf(PropTypes.object),
  textOverrides: PropTypes.shape({
    fitmentContinueCta: PropTypes.string
  })
};

export default FitmentTireSize;
